import {
  buildEnumReverseMaps,
  mapRowsToExposures,
  parseExposureFile,
} from "./catExposureUploadUtils";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import FileUpload from "components/FileUpload";
import { useCallback, useMemo, useState } from "react";

const MAX_PREVIEW_ROWS = 50;

const buildEnumForwardMaps = (catExposuresConfig, uploadConfig, catModel) => {
  const enums = catExposuresConfig?.enums ?? {};
  const columns = uploadConfig?.template?.columns ?? [];

  return Object.fromEntries(
    columns
      .filter((col) => col.field && (col.enumKey || col.values))
      .map((col) => {
        if (col.values) {
          return [col.field, Object.fromEntries(col.values.map((v) => [v, v]))];
        }
        const enumKey =
          col.enumKey === "regionPeril" && catModel
            ? `regionPeril_${catModel}`
            : col.enumKey;
        return [
          col.field,
          Object.fromEntries(
            (enums[enumKey]?.choices ?? []).map(({ key, name }) => [key, name])
          ),
        ];
      })
  );
};

const PreviewTable = ({ exposures, previewColumns, enumForwardMaps }) => {
  const displayValue = (col, value) => {
    if (value == null) return "";
    if (col.type === "percent") return `${(value * 100).toFixed(2)}%`;
    if (col.type === "number")
      return typeof value === "number" ? value.toLocaleString() : value;
    if (col.enumKey || col.values)
      return enumForwardMaps?.[col.field]?.[value] ?? value;
    return value;
  };

  return (
    <TableContainer sx={{ maxHeight: 300 }}>
      <Table size={"small"} stickyHeader>
        <TableHead>
          <TableRow>
            {previewColumns.map((col) => (
              <TableCell key={col.field}>{col.header}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {exposures.slice(0, MAX_PREVIEW_ROWS).map((row) => (
            <TableRow key={row.id}>
              {previewColumns.map((col) => (
                <TableCell key={col.field}>
                  {displayValue(col, row[col.field])}
                </TableCell>
              ))}
            </TableRow>
          ))}
          {exposures.length > MAX_PREVIEW_ROWS && (
            <TableRow>
              <TableCell colSpan={previewColumns.length}>
                <Typography variant={"body2"} color={"text.secondary"}>
                  {`... and ${exposures.length - MAX_PREVIEW_ROWS} more rows`}
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const CatExposureUploadDialog = ({
  open,
  onClose,
  onConfirm,
  catExposuresConfig,
  uploadConfig,
  catModel,
}) => {
  const [mode, setMode] = useState("append");
  const [parsedExposures, setParsedExposures] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [uploadFeedback, setUploadFeedback] = useState(null);
  const previewColumns = useMemo(
    () => (uploadConfig?.template?.columns ?? []).filter((c) => c.field),
    [uploadConfig]
  );

  const enumForwardMaps = useMemo(
    () => buildEnumForwardMaps(catExposuresConfig, uploadConfig, catModel),
    [catExposuresConfig, uploadConfig, catModel]
  );

  const reset = useCallback(() => {
    setParsedExposures([]);
    setWarnings([]);
    setUploadFeedback(null);
    setMode("append");
  }, []);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  const handleDrop = useCallback(
    async (acceptedFiles) => {
      const file = acceptedFiles?.[0];
      if (!file) return;

      const hasFieldColumns = (uploadConfig?.template?.columns ?? []).some(
        (c) => c.field
      );
      if (!hasFieldColumns) {
        setParsedExposures([]);
        setWarnings([]);
        setUploadFeedback({
          severity: "error",
          message:
            "Upload template is not configured, please contact Cactus support.",
        });
        return;
      }

      setUploadFeedback(null);

      try {
        const rows = await parseExposureFile(file);
        const reverseMaps = buildEnumReverseMaps(
          catExposuresConfig,
          uploadConfig,
          catModel
        );
        const { exposures, warnings: parseWarnings } = mapRowsToExposures(
          rows,
          reverseMaps,
          uploadConfig
        );
        setParsedExposures(exposures);
        setWarnings(parseWarnings);

        if (exposures.length === 0 && parseWarnings.length === 0) {
          const emptyRowsHint =
            rows.length === 0
              ? "No data rows found after the header row."
              : "No rows contained data in the configured columns. Use the Template file and check column layout and dropdown values.";
          setUploadFeedback({
            severity: "info",
            message: emptyRowsHint,
          });
        }
      } catch (err) {
        setParsedExposures([]);
        setWarnings([]);
        setUploadFeedback({
          severity: "error",
          message:
            err?.message != null
              ? String(err.message)
              : "Could not read the file, please use a valid .xlsx file.",
        });
      }
    },
    [catExposuresConfig, uploadConfig, catModel]
  );

  const handleConfirm = useCallback(() => {
    onConfirm(parsedExposures, mode);
    handleClose();
  }, [onConfirm, parsedExposures, mode, handleClose]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={"md"}
      fullWidth
      disableEnforceFocus
    >
      <DialogTitle>{"Upload Cat Exposures"}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <FileUpload
            onDrop={handleDrop}
            dropMessage={"Drop a .xlsx file here to upload"}
            asAtDateNotRequired
            title={""}
          />

          {uploadFeedback != null && (
            <Alert severity={uploadFeedback.severity}>
              <Typography variant={"body2"}>
                {uploadFeedback.message}
              </Typography>
            </Alert>
          )}

          <RadioGroup
            row
            value={mode}
            onChange={(e) => setMode(e.target.value)}
          >
            <FormControlLabel
              value={"append"}
              control={<Radio size={"small"} />}
              label={"Append to existing"}
            />
            <FormControlLabel
              value={"replace"}
              control={<Radio size={"small"} />}
              label={"Replace all"}
            />
          </RadioGroup>

          {warnings.length > 0 && (
            <Alert severity={"warning"}>
              <Typography variant={"body2"} sx={{ fontWeight: 600 }}>
                {`${warnings.length} unrecognized value(s):`}
              </Typography>
              {warnings.slice(0, 10).map((w, i) => (
                <Typography key={i} variant={"body2"}>
                  {w}
                </Typography>
              ))}
              {warnings.length > 10 && (
                <Typography variant={"body2"}>
                  {`... and ${warnings.length - 10} more`}
                </Typography>
              )}
            </Alert>
          )}

          {parsedExposures.length > 0 && (
            <Box>
              <Typography variant={"subtitle2"} sx={{ mb: 1 }}>
                {`Preview (${parsedExposures.length} row${
                  parsedExposures.length !== 1 ? "s" : ""
                })`}
              </Typography>
              <PreviewTable
                exposures={parsedExposures}
                previewColumns={previewColumns}
                enumForwardMaps={enumForwardMaps}
              />
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          disableElevation
          variant={"contained"}
          color={"secondary"}
        >
          {"Cancel"}
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          onClick={handleConfirm}
          disableElevation
          variant={"contained"}
          color={"primary"}
          disabled={parsedExposures.length === 0}
        >
          {`Import ${parsedExposures.length} row${
            parsedExposures.length !== 1 ? "s" : ""
          }`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CatExposureUploadDialog;
